import { useEffect } from 'react';

import { ACCESS_DENIED_EVENT } from '@/lib/api-client';
import { useToast } from '@/features/campaigns/toast-context';

/**
 * Surfaces an access-denied toast when an `/api/*` call is rejected with 403 under real auth
 * (SCRUM-103, D7). `apiFetch` dispatches {@link ACCESS_DENIED_EVENT} on the window (it is a plain
 * module and cannot use the toast hook); this component — mounted inside the app shell's
 * ToastProvider — listens and shows the toast. Rendering nothing, it exists only for the effect.
 *
 * Keeping the listener here (rather than in apiFetch) preserves the transport's UI-agnosticism and
 * ensures a forbidden response never renders as a silent empty view.
 */
export function AccessDeniedListener() {
  const { showToast } = useToast();

  useEffect(() => {
    const onDenied = () => {
      showToast({
        title: 'Access denied',
        body: "You don't have access to this. Contact your administrator if you believe this is an error.",
      });
    };
    window.addEventListener(ACCESS_DENIED_EVENT, onDenied);
    return () => {
      window.removeEventListener(ACCESS_DENIED_EVENT, onDenied);
    };
  }, [showToast]);

  return null;
}
